import { FIGHTER, tauntSlot, type AbilityDef, type FighterState, type InputBits } from "../sim";
import { BUTTON_BITS, stepBits, type Route } from "./routes";

/**
 * Исполнение пути бота (bot/routes.ts) по кадрам: сначала нажатия пути, потом звенья серии — следующая кнопка, когда
 * идёт нужный удар, направление ветки держится до следующего удара. Издёвка ждёт своего кадра. Не вышло — путь брошен.
 */
export interface Plan {
  readonly route: Route;
  /** Ближний приём: сначала подойти к сопернику на столько пикселей. */
  readonly approach?: number;
  step: number;
  link: number;
  wait: number;
}

/** Звено серии жмётся на этом кадре удара: нажатие запоминается до окна серии (sim SERIES_BUFFER). */
const LINK_AT = 3;
/** Столько кадров ждать нужного удара или кадра издёвки, потом бросить путь. */
const GIVE_UP = 45;

export const planOf = (route: Route, approach?: number): Plan => ({
  route,
  ...(approach !== undefined ? { approach } : {}),
  step: 0,
  link: 0,
  wait: 0,
});

/** Биты на следующий кадр мира; null — путь пройден или брошен. frame — кадр мира, который сейчас будет обработан. */
export function planInput(
  plan: Plan,
  me: FighterState,
  other: FighterState,
  kit: readonly AbilityDef[],
  frame: number,
): InputBits | null {
  const { route } = plan;
  if (plan.step === 0 && plan.approach !== undefined && Math.abs(other.x - me.x) / 256 > plan.approach) {
    plan.wait++;
    return plan.wait > GIVE_UP ? null : stepBits({ dir: "F", press: 0 }, me.facing);
  }
  const step = route.steps[plan.step];
  if (step) {
    const taunt = route.needs.taunt;
    if (taunt && step.press !== 0 && tauntSlot(frame, taunt.count) !== taunt.slot) {
      plan.wait++;
      return plan.wait > GIVE_UP ? null : 0;
    }
    plan.step++;
    return stepBits(step, me.facing);
  }
  const current = me.state === FIGHTER.ability ? kit[me.ability]?.id : undefined;
  const link = route.chain[plan.link];
  if (!link) {
    // все звенья нажаты: ветку выбирает зажатое направление в миг окна серии — держать его, пока не начнётся цель
    const last = route.chain.at(-1);
    if (!last || current === route.id) return null;
    plan.wait++;
    return plan.wait > GIVE_UP ? null : stepBits({ dir: last.dir, press: 0 }, me.facing);
  }
  const hold = stepBits({ dir: link.dir, press: 0 }, me.facing);
  if (current === link.after && me.abilityFrame >= LINK_AT) {
    plan.link++;
    plan.wait = 0;
    return stepBits({ dir: link.dir, press: BUTTON_BITS[link.button] }, me.facing);
  }
  plan.wait++;
  return plan.wait > GIVE_UP ? null : hold;
}
